export type RoundNumberOptions = {
  fallbackValue?: number;
};

const normalizePrecision = (precision: number | null | undefined): number => {
  const normalizedPrecision = Number(precision);
  if (!Number.isFinite(normalizedPrecision)) {
    return 0;
  }

  return Math.trunc(normalizedPrecision);
};

const shiftByExponent = (value: number, precision: number): number => {
  const [coefficient, exponent = "0"] = `${value}e`.split("e");
  return Number(`${coefficient}e${Number(exponent) + precision}`);
};

/**
 * Rounds a numeric value to a given precision.
 *
 * Behavior:
 * - Defaults to integer rounding when `precision` is not provided (or is `null`/`undefined`).
 * - Supports decimal precision (for example, `2`) and negative precision (for example, `-2`).
 * - Uses exponent shifting to avoid common floating-point rounding artifacts.
 * - Preserves signed zero (`-0`) when rounding negative values to zero.
 * - Returns `NaN` for non-finite inputs by default, or `fallbackValue` when provided.
 *
 * @param value Numeric value to round.
 * @param precision Decimal precision. Defaults to `0`.
 * @param options Additional rounding options.
 * @param options.fallbackValue Value returned when input/shifted value is non-finite.
 * @returns Rounded number.
 */
export const roundNumber = (
  value: number,
  precision: number | null | undefined = 0,
  options: RoundNumberOptions = {},
): number => {
  const fallbackValue = options.fallbackValue ?? NaN;

  if (!Number.isFinite(value)) {
    return fallbackValue;
  }

  const normalizedPrecision = normalizePrecision(precision);
  if (normalizedPrecision === 0) {
    return Math.round(value);
  }

  const shiftedValue = shiftByExponent(value, normalizedPrecision);
  if (!Number.isFinite(shiftedValue)) {
    return fallbackValue;
  }

  const roundedShiftedValue = Math.round(shiftedValue);
  if (Object.is(roundedShiftedValue, -0)) {
    return -0;
  }

  // after shifting back from precision math, js can produce 0 even when the input sign was negative
  const roundedValue = shiftByExponent(roundedShiftedValue, -normalizedPrecision);
  // if that happens we force -0 so callers that care about sign (like number inputs) do not lose it
  if (roundedValue === 0 && (value < 0 || Object.is(value, -0))) {
    return -0;
  }

  return roundedValue;
};
